import React, { useState } from 'react';
import {
	PaymentElement,
	useStripe,
	useElements,
} from '@stripe/react-stripe-js';
import {
	Box,
	Button,
	VStack,
	Text,
	Alert,
	AlertIcon,
	Spinner,
} from '@chakra-ui/react';
import { useAuthenticatedUserInfo } from '../../../../services/Contexts/AuthenticatedUserInfoContext';
import { useQueryClient } from '@tanstack/react-query';
import { confirmLifetimePurchase } from '../../../../Api/lifetimeSubscriptionApi';

const EmbeddedLifetimeCheckout = ({
	plan,
	paymentIntentId,
	onSuccess,
	onCancel,
}) => {
	const stripe = useStripe();
	const elements = useElements();
	const { userId, userEmail, companyId, token } = useAuthenticatedUserInfo();
	const queryClient = useQueryClient();
	const [error, setError] = useState(null);
	const [processing, setProcessing] = useState(false);

	const handleSubmit = async (event) => {
		event.preventDefault();
		if (!stripe || !elements || !plan) return;

		setProcessing(true);
		setError(null);

		try {
			const { error: stripeError, paymentIntent } = await stripe.confirmPayment({
				elements,
				confirmParams: {
					return_url: `${window.location.origin}/subscribe-to-plan`,
				},
				redirect: 'if_required',
			});

			if (stripeError) {
				throw new Error(stripeError.message);
			}

			const resolvedPaymentIntentId = paymentIntent?.id || paymentIntentId;
			if (!resolvedPaymentIntentId) {
				throw new Error('Payment could not be completed.');
			}

			const response = await confirmLifetimePurchase(
				{
					companyId,
					userId,
					userEmail,
					planId: plan._id || plan.id,
					paymentIntentId: resolvedPaymentIntentId,
				},
				token,
			);

			await queryClient.invalidateQueries({ queryKey: ['subscription', companyId] });
			await queryClient.invalidateQueries({ queryKey: ['companies', companyId] });

			onSuccess(response.data);
		} catch (err) {
			setError(err.response?.data?.error || err.response?.data?.message || err.message || 'Payment failed.');
		} finally {
			setProcessing(false);
		}
	};

	return (
		<Box as='form' onSubmit={handleSubmit}>
			<VStack spacing={5} align='stretch'>
				<PaymentElement
					options={{
						layout: 'tabs',
					}}
				/>

				{error && (
					<Alert status='error' borderRadius='lg'>
						<AlertIcon />
						{error}
					</Alert>
				)}

				<VStack spacing={3}>
					<Button
						type='submit'
						colorScheme='orange'
						size='lg'
						w='full'
						borderRadius='xl'
						isLoading={processing}
						loadingText='Processing payment…'
						isDisabled={!stripe || !elements || processing}>
						Pay once · unlock lifetime access
					</Button>
					<Button variant='ghost' w='full' onClick={onCancel} isDisabled={processing}>
						Cancel
					</Button>
				</VStack>

				<Text fontSize='xs' color='gray.500' textAlign='center' mb={0}>
					Secure one-time payment via Stripe. You stay in OpsPilot — no redirect.
				</Text>
			</VStack>
		</Box>
	);
};

export default EmbeddedLifetimeCheckout;
